import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, IndianRupee, FileSearch, Receipt, MessageSquareWarning, FileText, ShieldCheck, ArrowRight, Landmark, Loader2, Link2Off } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { api } from "../lib/api";
import { useAuth, homePathFor } from "../context/AuthContext";

// Phase 223: SMS short links are `/?id=CODE` (letters + digits only). The landing page resolves the code and
// moves on to the page the link stands for (demand notice PDF, receipt, password reset).
const SHORT_CODE_RE = /^[A-Za-z0-9]{4,32}$/;

const ShortLinkScreen = ({ state, onHome }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="short-link-screen">
    <Card className="w-full max-w-md shadow-sm">
      <CardContent className="p-8 text-center space-y-4">
        {state === "opening" ? (
          <>
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" aria-hidden="true" />
            <p className="font-semibold font-heading text-lg" data-testid="short-link-opening">Opening your link…</p>
            <p className="text-sm text-muted-foreground">कृपया प्रतीक्षा करें — आपका लिंक खुल रहा है।</p>
          </>
        ) : (
          <>
            <div className="h-12 w-12 mx-auto rounded-full bg-muted flex items-center justify-center">
              <Link2Off className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            </div>
            <p className="font-semibold font-heading text-lg" data-testid="short-link-invalid">This link is not valid</p>
            <p className="text-sm text-muted-foreground">
              Please check the link in your SMS and try again, or contact your municipality office.
            </p>
            <p className="text-sm text-muted-foreground">यह लिंक मान्य नहीं है। कृपया SMS में दिया लिंक जाँचें।</p>
            <Button onClick={onHome} data-testid="short-link-home">Go to home page</Button>
          </>
        )}
      </CardContent>
    </Card>
  </div>
);

const tiles = [
  { icon: IndianRupee, title: "Pay Property Tax", desc: "Search your property and pay dues online — no login needed", to: "/pay", testId: "tile-pay-property-tax" },
  { icon: FileSearch, title: "Search Property", desc: "Look up any property by its PIN and view outstanding dues" },
  { icon: Receipt, title: "Download Receipts", desc: "Print or download official payment receipts anytime" },
  { icon: MessageSquareWarning, title: "Raise Grievance", desc: "Report civic issues and track resolution with SLA" },
  { icon: FileText, title: "Apply for Services", desc: "Birth certificate, trade license and more municipal services" },
  { icon: ShieldCheck, title: "Self Assessment", desc: "Declare a new property and get an instant tax estimate" },
];

export default function Landing() {
  const [munis, setMunis] = useState([]);
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const shortId = (params.get("id") || "").trim();
  const [linkState, setLinkState] = useState(shortId ? "opening" : null);

  // Phase 223: `/?id=CODE` → ask the server where the code points and go there (same origin)
  useEffect(() => {
    if (!shortId) return;
    if (!SHORT_CODE_RE.test(shortId)) { setLinkState("invalid"); return; }
    let alive = true;
    api.get(`/public/l/${encodeURIComponent(shortId)}`)
      .then((r) => {
        const path = r.data?.path || r.data?.url;
        if (!alive) return;
        if (path) window.location.replace(path);
        else setLinkState("invalid");
      })
      .catch(() => { if (alive) setLinkState("invalid"); });
    return () => { alive = false; };
  }, [shortId]);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (shortId) return;   // a short link always wins over the signed-in redirect
    if (auth?.user) navigate(homePathFor(auth));
    // eslint-disable-next-line
  }, []);

  if (linkState) {
    return <ShortLinkScreen state={linkState} onHome={() => { setLinkState(null); const p = new URLSearchParams(params); p.delete("id"); setParams(p, { replace: true }); }} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold font-heading leading-tight">MunicipalConnect</p>
              <p className="text-[11px] text-muted-foreground leading-tight">National Municipal Services Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild data-testid="staff-login-link">
              <Link to="/login">Staff Login</Link>
            </Button>
            <Button asChild data-testid="citizen-login-link">
              <Link to="/citizen">Citizen Login</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="civic-mist border-b">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-xs font-medium bg-card border rounded-full px-3 py-1 mb-4">
              <Landmark className="h-3.5 w-3.5" /> Government of India • Digital Municipal Governance
            </p>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight font-heading leading-tight">
              One platform for every municipal service
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-4">
              Pay property tax, apply for certificates, raise grievances and track everything online.
              Trusted by municipal corporations across India with secure, independent city servers.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button size="lg" asChild data-testid="hero-pay-cta">
                <Link to="/pay"><IndianRupee className="h-4 w-4 mr-2" /> Pay Property Tax</Link>
              </Button>
              <Button size="lg" variant="outline" asChild data-testid="hero-citizen-cta">
                <Link to="/citizen">Citizen Portal <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
              <Button size="lg" variant="ghost" asChild data-testid="hero-staff-cta">
                <Link to="/login">Municipal Staff Login</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-xl sm:text-2xl font-semibold font-heading mb-6">Citizen services at your fingertips</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t) => {
            const body = (
              <CardContent className="p-5">
                <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center mb-3">
                  <t.icon className="h-5 w-5 text-accent-foreground" />
                </div>
                <p className="font-semibold inline-flex items-center gap-1.5">{t.title}{t.to && <ArrowRight className="h-4 w-4 text-primary" />}</p>
                <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
              </CardContent>
            );
            return t.to ? (
              <Link key={t.title} to={t.to} className="block rounded-xl focus-visible:ring-2 focus-visible:ring-ring" data-testid={t.testId}>
                <Card className="shadow-sm hover:shadow-md hover:border-primary/40 transition-shadow h-full">{body}</Card>
              </Link>
            ) : (
              <Card key={t.title} className="shadow-sm hover:shadow-md transition-shadow">{body}</Card>
            );
          })}
        </div>
      </section>

      <section className="bg-card border-t">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-xl sm:text-2xl font-semibold font-heading mb-2">Participating municipalities</h2>
          <p className="text-sm text-muted-foreground mb-6">Each municipality runs on its own secure government server with an independent database.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="municipality-list">
            {munis.map((m) => (
              <Card key={m.id} className="shadow-sm">
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{m.name}</p>
                    <p className="text-sm text-muted-foreground">{m.district ? `${m.district}, ` : ""}{m.state}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" asChild data-testid={`muni-pay-${m.code}`}>
                      <Link to={`/${m.code}/pay`}>Pay Tax</Link>
                    </Button>
                    <Button size="sm" variant="outline" asChild data-testid={`muni-portal-${m.code}`}>
                      <Link to={`/citizen?muni=${m.code}`}>Portal</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {munis.length === 0 && <p className="text-sm text-muted-foreground">No municipalities onboarded yet.</p>}
          </div>
        </div>
      </section>

      <footer className="border-t py-6 text-center text-xs text-muted-foreground">
        MunicipalConnect — Data hosted in India • Compliant with IT Act 2000 &amp; DPDP Act 2023
      </footer>
    </div>
  );
}
